/**
 * Os passos do wizard de ambiente novo (spec 2026-10-09 §5, tabela de passos).
 * O passo 2 (prontidão da conta) mora em `PassoProntidao.tsx`.
 *
 * Cada passo só dispõe e edita o rascunho por `useRascunho` — nenhum passo
 * guarda estado próprio do valor, para o chat e o modal verem o mesmo dado.
 */
import { Group, MultiSelect, Stack, Text, Title } from '@mantine/core'
import { IconAlertTriangle, IconCircleCheck } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { candidatosAAcompanhar, lerRedeDoRascunho, type ServicoDoRascunho } from '@/api/rascunhoDeAmbiente'
import { CHAVE_PEDIDO_TERRAFORM } from '@/hooks/useTerraformChangeRequest'
import { Dado } from '../components/Dado'
import { ResumoDaCriacao } from '../components/ResumoDaCriacao'
import { Campo } from './Campo'
import { EscolhaDoAmbiente } from './EscolhaDoAmbiente'
import { HostsDoAmbiente } from './HostsDoAmbiente'
import { estadoDaQuery, LeituraDoDevops } from './LeituraDoDevops'
import type { UseRascunho } from './useRascunho'

const BARLOW = { fontFamily: '"Barlow", Arial, sans-serif' }

export interface PassoProps {
  r: UseRascunho
  tenantId: number
  rascunhoId: number
}

function Rotulo({ children }: { children: React.ReactNode }) {
  return <Text size="xs" c="dimmed" style={BARLOW}>{children}</Text>
}

// ── 1 Ambiente e conta ─────────────────────────────────────────────────────

export function PassoTenant(props: PassoProps) {
  const { r } = props
  const x = r.rascunho
  if (!x) return null
  return (
    <Stack gap="sm">
      <Stack gap={2}><Rotulo>Projeto</Rotulo><Dado>{x.projeto}</Dado></Stack>
      <EscolhaDoAmbiente {...props} />
      <Stack gap={2}>
        <Rotulo>Organização</Rotulo>
        <Text size="sm" style={BARLOW}>{x.organizacao?.nome ?? 'Tenant sem organização vinculada'}</Text>
      </Stack>
      <Stack gap={2}>
        <Rotulo>Tenant do {x.ambiente}</Rotulo>
        <Text size="sm" style={BARLOW}>{x.tenant.nome} <Dado>#{x.tenant.id}</Dado></Text>
      </Stack>
      <Campo r={r} caminho="account_id" rotulo="Conta AWS (fixa: a do tenant)" tipo="fixo" />
    </Stack>
  )
}

// ── 3 Projeto e rede ───────────────────────────────────────────────────────

export function PassoProjetoRede({ r, tenantId, rascunhoId }: PassoProps) {
  const bloco = r.valor('bloco_16')
  const rede = useQuery({
    queryKey: [CHAVE_PEDIDO_TERRAFORM, 'formulario-rede', tenantId, rascunhoId, r.rascunho?.versao],
    queryFn: () => lerRedeDoRascunho(tenantId, rascunhoId),
    enabled: Boolean(r.rascunho),
    retry: false,
  })
  let leitura = estadoDaQuery(rede)
  let texto: string | undefined
  let motivo: string | undefined
  if (leitura === 'ok' && rede.data) {
    if (rede.data.estado === 'nao_conferido') {
      leitura = 'erro'
      motivo = rede.data.motivo
    } else if (rede.data.estado === 'sem_valor') {
      texto = 'Sem bloco para conferir no IPAM ainda.'
    } else {
      const quando = rede.data.visto_em ? new Date(rede.data.visto_em).toLocaleString('pt-BR') : 'sem data'
      texto = rede.data.colisoes?.length
        ? `IPAM (lido em ${quando}): colide com ${rede.data.colisoes.join(', ')}`
        : `IPAM (lido em ${quando}): ${rede.data.bloco_16} sem colisão`
    }
  } else if (leitura === 'erro') {
    motivo = 'o @DevOps não respondeu'
  }
  return (
    <Stack gap="sm">
      <Stack gap={4}>
        <Campo r={r} caminho="dominio" rotulo="Domínio do projeto (sem o ambiente)"
               descricao={`O ${r.rascunho?.ambiente ?? 'ambiente'} entra sozinho em cada endereço; veja abaixo como ficam.`} />
        <HostsDoAmbiente r={r} tenantId={tenantId} rascunhoId={rascunhoId} />
      </Stack>
      <Stack gap={4}>
        <Campo r={r} caminho="bloco_16" rotulo="Bloco /16 do projeto" />
        {bloco ? (
          <LeituraDoDevops estado={leitura} texto={texto} motivo={motivo}
                           onTentarDeNovo={() => { void rede.refetch() }} />
        ) : null}
      </Stack>
      <Campo r={r} caminho="repositorio_iac" rotulo="Repositório IaC (GitLab)" />
      <Campo r={r} caminho="role_stage" rotulo="Role do stage na conta" />
      <Group gap="xl" wrap="wrap" align="flex-start">
        <Campo r={r} caminho="regiao" rotulo="Região" tipo="escolha" opcoes={r.rascunho?.opcoes.regiao}
               descricao="Só as regiões que o molde aceita." />
        <Campo r={r} caminho="peer_tgw" rotulo="Ligado ao Transit Gateway" tipo="fixo" />
      </Group>
    </Stack>
  )
}

// ── 4 Serviços ─────────────────────────────────────────────────────────────

function chaveDe(s: ServicoDoRascunho, i: number): string {
  return String(s.chave ?? s.nome ?? i).toLowerCase()
}

export function PassoServicos({ r }: PassoProps) {
  const servicos = r.rascunho?.campos.servicos ?? []
  const op = r.rascunho?.opcoes ?? {}
  const lim = r.rascunho?.limites ?? {}
  if (!servicos.length) {
    return <Text size="sm" style={BARLOW}>Nenhum serviço no rascunho — descreva-os na conversa.</Text>
  }
  return (
    <Stack gap="lg">
      {servicos.map((s, i) => {
        const p = `servicos[${chaveDe(s, i)}]`
        return (
          <Stack key={p} gap="xs" component="section" aria-label={`Serviço ${s.nome ?? chaveDe(s, i)}`}
                 style={{ borderTop: '1px solid var(--mantine-color-default-border)', paddingTop: 12 }}>
            <Title order={5} style={BARLOW}>{String(s.nome ?? chaveDe(s, i))}</Title>
            <Group gap="md" grow wrap="wrap" align="flex-start">
              <Campo r={r} caminho={`${p}.papel`} rotulo="Papel" tipo="escolha" opcoes={op.papel} />
              <Campo r={r} caminho={`${p}.exposicao`} rotulo="Exposição" tipo="escolha" opcoes={op.exposicao} />
              <Campo r={r} caminho={`${p}.porta`} rotulo="Porta na instância" tipo="numero"
                     limites={lim.porta} />
            </Group>
            <Group gap="md" grow wrap="wrap" align="flex-start">
              <Campo r={r} caminho={`${p}.tipo_instancia`} rotulo="Tipo da instância (EC2)" tipo="escolha"
                     opcoes={op.tipo_instancia} />
              <Campo r={r} caminho={`${p}.disco_gb`} rotulo="Disco (GB)" tipo="numero"
                     limites={lim.disco_gb} />
              <Campo r={r} caminho={`${p}.capacidade_inicial`} rotulo="Instâncias no início" tipo="numero"
                     limites={lim.capacidade_inicial} />
            </Group>
            <Campo r={r} caminho={`${p}.host`} rotulo="Host" />
            {r.valor(`${p}.papel`) === 'servico' && ['alb', 'kong'].includes(String(r.valor(`${p}.exposicao`))) && (
              <Campo r={r} caminho={`${p}.saude_caminho`} rotulo="Caminho de saúde (responde 200)"
                     descricao="Ex.: /api-acl/ping. A barra (/) deu 404 nas APIs do family-office." />
            )}
            <Campo r={r} caminho={`${p}.usa_banco`} rotulo="Ganha database no RDS" tipo="sim_nao" />
            <Campo r={r} caminho={`${p}.monitoramento`} rotulo="Monitoramento do Zabbix no serviço"
                   tipo="escolha" opcoes={op.monitoramento}
                   descricao={'rabbitmq, redis ou mongodb: o ConsoleAI cria o segredo '
                              + '<projeto>-<ambiente>/zabbix/<tipo> com usuário e senha; o usuário '
                              + 'dentro do serviço é criado pela aplicação com essa senha.'} />
            <Group gap="md" grow wrap="wrap" align="flex-start">
              <Campo r={r} caminho={`${p}.repositorio_devops`} rotulo="Repositório de deploy (devops/…)" />
              <Campo r={r} caminho={`${p}.repositorio_app`} rotulo="Repositório da aplicação" />
            </Group>
            <Campo r={r} caminho={`${p}.instancia`} rotulo="Instância compartilhada"
                   descricao={'Serviços com o mesmo nome aqui sobem na mesma máquina, cada um no seu '
                              + 'contêiner e porta. Eles precisam do mesmo tipo, disco, instâncias no '
                              + 'início e repositório de deploy. Vazio: máquina própria.'} />
          </Stack>
        )
      })}
    </Stack>
  )
}

// ── 5 Banco ────────────────────────────────────────────────────────────────

export function PassoBanco({ r }: PassoProps) {
  const op = r.rascunho?.opcoes ?? {}
  const lim = r.rascunho?.limites ?? {}
  const engine = r.valor('banco.engine')
  // Sem o bloco gravado o stage não tem CloudHSM: o padrão do contrato é `nenhum`.
  const modoDoHsm = r.valor('cloudhsm.modo') ?? 'nenhum'
  return (
    <Stack gap="sm">
      <Campo r={r} caminho="banco.engine" rotulo="Engine" tipo="escolha" opcoes={op.engine} />
      {engine !== 'nenhum' && (
        <>
          <Group gap="md" grow wrap="wrap" align="flex-start">
            <Campo r={r} caminho="banco.engine_version" rotulo="Versão" tipo="escolha"
                   opcoes={op.engine_version} />
            <Campo r={r} caminho="banco.instance_class" rotulo="Classe" tipo="escolha"
                   opcoes={op.instance_class} />
            <Campo r={r} caminho="banco.storage_gb" rotulo="Disco (GB)" tipo="numero"
                   descricao="Padrão: 100 GB por banco (um database por serviço que usa banco)."
                   limites={lim.storage_gb} />
          </Group>
          <Campo r={r} caminho="banco.multi_az" rotulo="Multi-AZ" tipo="sim_nao"
                 descricao="Réplica em outra zona; custa o dobro. Ainda não aplicado: o módulo do banco não lê este campo, e o banco nasce numa zona só." />
        </>
      )}
      <Stack gap="sm" component="section" aria-label="CloudHSM">
        <Campo r={r} caminho="cloudhsm.modo" rotulo="CloudHSM" tipo="escolha"
               opcoes={op.cloudhsm_modo}
               descricao="Cluster de HSMs do stage. FIPS ou NON_FIPS não mudam depois: trocar substitui o cluster e as chaves se perdem."
               aoEscolher={(v) => { if (v === 'nenhum') r.editar('cloudhsm.hsms', null) }} />
        {modoDoHsm !== 'nenhum' && (
          <Campo r={r} caminho="cloudhsm.hsms" rotulo="HSMs" tipo="numero"
                 descricao="Um por zona. O cluster nasce sem inicializar: ativar é manual, depois do apply."
                 limites={lim.cloudhsm_hsms} />
        )}
      </Stack>
    </Stack>
  )
}

// ── 6 Quem acompanha ───────────────────────────────────────────────────────

export function PassoQuemAcompanha({ r, tenantId, rascunhoId }: PassoProps) {
  const q = useQuery({
    queryKey: [CHAVE_PEDIDO_TERRAFORM, 'formulario-acompanhantes', tenantId, rascunhoId],
    queryFn: () => candidatosAAcompanhar(tenantId, rascunhoId),
    enabled: Boolean(r.rascunho),
    retry: false,
  })
  const atuais = (r.valor('acompanhantes') as number[] | undefined) ?? []
  const estado = estadoDaQuery(q)
  return (
    <Stack gap="sm">
      <Text size="sm" style={BARLOW}>
        Quem recebe a entrega do ambiente além de quem pediu e do gestor da organização. Só
        aparece quem tem acesso a este tenant ou é o gestor da organização dele.
      </Text>
      {estado !== 'ok' ? (
        <LeituraDoDevops estado={estado} motivo="a lista de pessoas não carregou"
                         onTentarDeNovo={() => { void q.refetch() }} />
      ) : (
        <MultiSelect
          label={<Text size="sm" fw={500} style={BARLOW} span>Quem acompanha</Text>}
          searchable clearable
          data={(q.data?.items ?? []).map((c) => ({
            value: String(c.user_id),
            label: c.gestor ? `${c.nome} (gestor da organização)` : c.nome,
          }))}
          value={atuais.map(String)}
          onChange={(v) => r.definirAcompanhantes(v.map(Number))}
        />
      )}
    </Stack>
  )
}

// ── 7 Revisão ──────────────────────────────────────────────────────────────

export const ETAPAS_DEPOIS_DO_CONFIRMAR = [
  'Abre o chamado de registro do pedido',
  'Roda o plan na conta do tenant e mostra o parecer do @DevOps',
  'Espera a aprovação de quem decide',
  'Merge do MR no repositório IaC',
  'Apply na conta',
  'Entrega: ficha do ambiente, documento na KB e aviso a quem pediu, ao gestor e a quem acompanha',
]

export interface Bloqueio {
  chave: string
  texto: string
}

export function bloqueiosDoRascunho(r: UseRascunho): Bloqueio[] {
  const x = r.rascunho
  if (!x) return []
  return [
    ...x.faltando.map((f) => ({ chave: `falta:${f}`, texto: `Falta preencher ${f}` })),
    ...x.invalidos.map((i) => ({ chave: `invalido:${i.campo}`, texto: `${i.campo}: ${i.o_que_dizer}` })),
  ]
}

export function PassoRevisao({ r, bloqueios }: PassoProps & { bloqueios: Bloqueio[] }) {
  const x = r.rascunho
  if (!x) return null
  return (
    <Stack gap="md">
      {bloqueios.length > 0 ? (
        <Stack gap={4} role="alert">
          <Group gap={6} wrap="nowrap">
            <IconAlertTriangle size={16} stroke={1.5} color="var(--mantine-color-yellow-8)" aria-hidden />
            <Text size="sm" fw={600} style={BARLOW}>O Confirmar está bloqueado porque:</Text>
          </Group>
          {bloqueios.map((b) => <Text key={b.chave} size="sm" style={BARLOW}>{b.texto}</Text>)}
        </Stack>
      ) : (
        <Group gap={6} wrap="nowrap">
          <IconCircleCheck size={16} stroke={1.5} color="var(--mantine-color-teal-7)" aria-hidden />
          <Text size="sm" style={BARLOW}>Nada bloqueia o Confirmar.</Text>
        </Group>
      )}
      <Stack gap={4}>
        <Text size="sm" fw={600} style={BARLOW}>O que será criado</Text>
        {x.resumo ? <ResumoDaCriacao resumo={x.resumo} />
          : <Text size="sm" c="dimmed" style={BARLOW}>O inventário sai quando nada faltar.</Text>}
      </Stack>
      <Stack gap={4}>
        <Text size="sm" fw={600} style={BARLOW}>Depois do Confirmar</Text>
        {ETAPAS_DEPOIS_DO_CONFIRMAR.map((e, i) => (
          <Text key={e} size="sm" style={BARLOW}><Dado>{i + 1}.</Dado> {e}</Text>
        ))}
      </Stack>
      {x.acompanhantes.length > 0 && (
        <Stack gap={2}>
          <Text size="sm" fw={600} style={BARLOW}>Quem acompanha</Text>
          <Text size="sm" style={BARLOW}>{x.acompanhantes.map((a) => a.nome).join(', ')}</Text>
        </Stack>
      )}
    </Stack>
  )
}
